import { Typography, Stack, Box, Button, useMediaQuery } from '@mui/material';
import { PropsWithChildren } from 'react';
import ResetReaderConfigurationButton from './ResetReaderConfigurationButton';
import ConfigSlider from './ConfigSlider';
import { useTranslation } from 'react-i18next';
import {
  lineHeightMap,
  lineLengthMap,
  paragraphSpacingMap,
} from './utils/ConfigUtils';
import { useBookMenuContext } from '../../components/BookMenuContext';

function ConfigSection({
  headingText,
  children,
}: PropsWithChildren<{ headingText: string }>) {
  return (
    <Box component='fieldset' sx={{ border: 'none', padding: 0 }}>
      <Typography
        component='legend'
        variant='subtitle2'
        sx={{ fontWeight: 'bold', padding: 0, marginBottom: 2 }}
      >
        {headingText}
      </Typography>
      {children}
    </Box>
  );
}

const smallQcBreakpoint = '@';
const wideQcBreakpoint = '@500';

export default function ReaderConfigurationMenu() {
  const { t } = useTranslation();
  const showApplyButton = useMediaQuery((theme) =>
    theme.breakpoints.down('lg'),
  );
  const { selectMenuView } = useBookMenuContext();

  return (
    <Box
      component='form'
      aria-labelledby='reader-configuration-menu-title'
      sx={{ marginTop: 3, paddingX: 2 }}
    >
      <ConfigSection headingText={'Teksti'}>
        <Box sx={{ containerType: 'inline-size' }}>
          <Stack
            gap={1.5}
            sx={{
              flexDirection: {
                [smallQcBreakpoint]: 'column',
                [wideQcBreakpoint]: 'row',
              },
              gap: { [smallQcBreakpoint]: 1.5, [wideQcBreakpoint]: 3 },
              marginBottom: 1.5,
            }}
          >
            <Stack direction='column' gap={1.5} flex={1}>
              <ConfigSlider
                id='line-length-slider'
                configKey='lineLength'
                inputLabel='Palstan leveys'
                range={{ min: 1, max: 3, step: 1 }}
                valueLabelFormat={(value) =>
                  t(
                    `readerConfiguration.lineLength.label.${lineLengthMap.get(value)?.key}`,
                  )
                }
                marks={true}
              />

              <ConfigSlider
                id='font-size-slider'
                configKey='fontSizePercentage'
                inputLabel='Fonttikoko'
                range={{ min: 75, max: 200, step: 1 }}
                unitLabel=' %'
              />

              <ConfigSlider
                id='line-height-slider'
                configKey='lineHeight'
                inputLabel='Riviväli'
                range={{ min: 1, max: 5, step: 1 }}
                valueLabelFormat={(value) =>
                  t(
                    `readerConfiguration.lineHeight.label.${lineHeightMap.get(value)?.key}`,
                  )
                }
                marks={true}
              />
            </Stack>

            <Stack direction='column' gap={1.5} flex={1}>
              <ConfigSlider
                id='letter-spacing-slider'
                configKey='letterSpacing'
                inputLabel='Merkkiväli'
                range={{ min: 0, max: 100, step: 1 }}
                unitLabel=' %'
              />

              <ConfigSlider
                id='word-spacing-slider'
                configKey='wordSpacing'
                inputLabel='Sanaväli'
                range={{ min: 0, max: 100, step: 1 }}
                unitLabel=' %'
              />
              <ConfigSlider
                id='paragraph-spacing-slider'
                configKey='paragraphSpacing'
                inputLabel='Kappaleväli'
                range={{ min: 1, max: 3, step: 1 }}
                marks={true}
                valueLabelFormat={(value) =>
                  t(
                    `readerConfiguration.paragraphSpacing.label.${paragraphSpacingMap.get(value)?.key}`,
                  )
                }
              />
            </Stack>
          </Stack>

          <Stack
            sx={{
              flexDirection: {
                [smallQcBreakpoint]: 'column',
                [wideQcBreakpoint]: 'row',
              },
              gap: { [smallQcBreakpoint]: 1.5, [wideQcBreakpoint]: 3 },
            }}
          >
            <Stack direction='column' gap={1.5} flex={1}>
              <ResetReaderConfigurationButton />
            </Stack>
            {showApplyButton && (
              <Stack direction='column' gap={1.5} flex={1}>
                <Button
                  variant='contained'
                  size='large'
                  sx={{ textTransform: 'none' }}
                  onClick={() => selectMenuView(null)}
                >
                  {t('readerConfiguration.apply')}
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>
      </ConfigSection>
    </Box>
  );
}
